import { Badge, Banner, Button, Dialog, Input } from '@cloudflare/kumo';
import { FormEvent, useEffect, useState } from 'react';
import { ICONS } from '../ui/icons';

/** Tag no formato do Atlas: chave e valor (ex.: ambiente: produção) */
export interface KeyValueTag { key: string; value: string }

export const tagText = (t: KeyValueTag) => `${t.key}: ${t.value}`;

/** Célula "Tags": chips ou "+ Adicionar tags" (quem não pode editar só vê) */
export function TagsCell({ tags, canEdit, onManage, max = 3 }: { tags: KeyValueTag[]; canEdit: boolean; onManage: () => void; max?: number }) {
  if (!tags.length) {
    return canEdit ? <button type="button" className="atlas-link" onClick={onManage}>+ Adicionar tags</button> : <span className="atlas-muted">—</span>;
  }
  const extra = tags.length - max;
  return (
    <div className="atlas-tags">
      {tags.slice(0, max).map((t) => <span key={t.key} title={tagText(t)}><Badge variant="neutral" className="atlas-tag">{tagText(t)}</Badge></span>)}
      {extra > 0 && <span className="atlas-muted" title={tags.slice(max).map(tagText).join('\n')}>+{extra}</span>}
      {canEdit && (
        <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label="Gerenciar tags" className="atlas-tag-edit" onClick={onManage} />
      )}
    </div>
  );
}

/**
 * Modal "Gerenciar tags" (como "Manage Tags" no Atlas): linhas de chave e
 * valor, adicionar e remover; salva a lista inteira de uma vez.
 */
export function ManageTagsModal({ open, title, subject, tags, onClose, onSave }: {
  open: boolean;
  title?: string;
  subject: string;
  tags: KeyValueTag[];
  onClose: () => void;
  onSave: (tags: KeyValueTag[]) => Promise<void>;
}) {
  const [rows, setRows] = useState<KeyValueTag[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recarrega só ao abrir (a lista recebida muda de identidade a cada render da página)
  useEffect(() => {
    if (open) { setRows(tags.length ? tags.map((t) => ({ ...t })) : [{ key: '', value: '' }]); setError(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (i: number, patch: Partial<KeyValueTag>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const filled = rows.filter((r) => r.key.trim() || r.value.trim());
  const keys = filled.map((r) => r.key.trim().toLowerCase());
  const problem = filled.some((r) => !r.key.trim() || !r.value.trim())
    ? 'Preencha a chave e o valor de cada tag (ou remova a linha).'
    : keys.some((k, i) => keys.indexOf(k) !== i) ? 'Há chaves repetidas.' : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problem) return;
    setSaving(true); setError(null);
    try {
      await onSave(filled.map((r) => ({ key: r.key.trim(), value: r.value.trim() })));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar as tags');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">{title || 'Gerenciar tags'}</h3>}</Dialog.Title>

      <form id="manage-tags-form" onSubmit={submit}>
        <div className="text-kumo-subtle" style={{ marginBottom: 20 }}>
          Tags de <strong>{subject}</strong>: pares de chave e valor para organizar e filtrar (ex.: <em>ambiente: produção</em>, <em>time: vendas</em>).
        </div>
        {error && <div style={{ marginBottom: 16 }}><Banner variant="error" description={<>{error}</>} /></div>}
        <div className="atlas-kv-grid">
          <div className="font-semibold">Chave</div><div className="font-semibold">Valor</div><span />
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <Input onChange={(e) => set(i, { key: e.target.value })} aria-label={`Chave da tag ${i + 1}`} placeholder="ambiente" value={r.key} maxLength={64} />
              <Input onChange={(e) => set(i, { value: e.target.value })} aria-label={`Valor da tag ${i + 1}`} placeholder="produção" value={r.value} maxLength={128} />
              <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Remover tag ${i + 1}`} type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} />
            </div>
          ))}
        </div>
        <button type="button" className="atlas-link" style={{ marginTop: 12 }} onClick={() => setRows((r) => [...r, { key: '', value: '' }])} disabled={rows.length >= 50}>
          + Adicionar tag
        </button>
        {problem && filled.length > 0 && <div style={{ color: 'var(--crm-text-error)', marginTop: 12 }}>{problem}</div>}
      </form>
    <div className="mt-6 flex justify-end gap-2">{<div className="float-right">
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="primary" form="manage-tags-form" loading={saving} disabled={!!problem}>Salvar</Button>
          </div>
        </div>}</div>
</Dialog>
</Dialog.Root>
  );
}
